/**
 * CalendarMiniCalendar - Mini calendário do sidebar da página de Calendário
 *
 * Exibe um calendário compacto (shadcn `<Calendar>`) marcando com um ponto
 * os dias que possuem eventos. Ao clicar em um dia, sinaliza para o pai
 * navegar o FullCalendar para aquela data (em visão Dia). Ao trocar o mês,
 * sincroniza o FullCalendar.
 *
 * RELEASE:
 *   2026-06-24 v1.1.0 - Refatorado para se ajustar fluidamente à coluna do
 *                       sidebar (sem Card), células compactas e header inline.
 *   2026-06-24 v1.0.0 - Versão inicial (mini calendário no sidebar)
 */

import { useMemo, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { ptBR } from 'date-fns/locale';

interface CalendarMiniCalendarProps {
  /** Datas (locais) que possuem ao menos um evento — destacadas com um ponto. */
  eventDates: Date[];
  /** Data selecionada atualmente. */
  selectedDate?: Date;
  /** Disparado ao clicar em um dia do mini calendário. */
  onSelectDate: (date: Date | undefined) => void;
  /** Disparado quando o usuário navega para outro mês. */
  onMonthChange?: (date: Date) => void;
}

/**
 * Mini calendário compacto exibido na sidebar do Calendário Global.
 *
 * Sem `<Card>` para ocupar toda a largura da coluna do sidebar e evitar
 * padding duplo. Células reduzidas (h-7/w-7) para caber em colunas de ~240px.
 */
export function CalendarMiniCalendar({
  eventDates,
  selectedDate,
  onSelectDate,
  onMonthChange,
}: CalendarMiniCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState<Date>(selectedDate ?? new Date());

  // Modificador "hasEvent" para destacar dias com eventos
  const modifiers = useMemo(() => ({ hasEvent: eventDates }), [eventDates]);

  const modifiersClassNames = {
    hasEvent:
      'relative after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-primary',
  };

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
    onMonthChange?.(date);
  };

  return (
    <div className="w-full rounded-md border bg-card">
      {/* Header inline compacto */}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b">
        <CalendarIcon className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Calendário
        </span>
      </div>

      <div className="p-1.5">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={onSelectDate}
          month={currentMonth}
          onMonthChange={handleMonthChange}
          locale={ptBR}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          className="p-0 w-full pointer-events-auto"
          classNames={{
            months: 'flex w-full',
            month: 'w-full space-y-2 relative',
            month_caption: 'flex justify-center pt-0.5 relative items-center h-7',
            caption_label: 'text-xs font-medium',
            nav: 'flex items-center justify-between absolute inset-x-0 top-0 z-10 px-0.5 pt-0.5',
            button_previous:
              'h-6 w-6 bg-transparent p-0 opacity-60 hover:opacity-100 inline-flex items-center justify-center rounded-md hover:bg-accent border border-input',
            button_next:
              'h-6 w-6 bg-transparent p-0 opacity-60 hover:opacity-100 inline-flex items-center justify-center rounded-md hover:bg-accent border border-input',
            month_grid: 'w-full border-collapse',
            weekdays: 'flex w-full',
            weekday:
              'text-muted-foreground rounded-md flex-1 font-normal text-[10px] uppercase',
            week: 'flex w-full mt-1',
            day: 'flex-1 h-7 text-center text-xs p-0 relative focus-within:relative focus-within:z-20',
            day_button:
              'h-7 w-full p-0 font-normal text-xs aria-selected:opacity-100 hover:bg-accent rounded-md inline-flex items-center justify-center',
            selected:
              'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary',
            today: 'bg-accent text-accent-foreground',
            outside: 'text-muted-foreground opacity-40',
            disabled: 'text-muted-foreground opacity-50',
          }}
        />
      </div>
    </div>
  );
}
